// External packages
import { createContext, useContext, useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

// Components
import { Icon } from 'components/Icons';
import { API_BASE_URL } from 'api/base';

// Types
type UserRole = 'ADMIN' | 'USER' | null;

interface UserRoleContextValue {
	isAdmin: boolean;
	isCashier: boolean;
	roleValue: UserRole;
}

const UserRoleContext = createContext<UserRoleContextValue>({
	isAdmin: false,
	isCashier: false,
	roleValue: null,
});

export const useUserRole = () => useContext(UserRoleContext);

export default function ProtectedRoute() {
	const [isAuthenticated, setIsAuthenticated] = useState<null | boolean>(null);
	const [userRole, setUserRole] = useState<UserRole>(null);

	const location = useLocation();

	useEffect(() => {
		const checkAuth = async () => {
			const authToken = localStorage.getItem('authToken');
			if (!authToken) {
				setIsAuthenticated(false);
				setUserRole(null);
				return;
			}
			try {
				const response = await fetch(
					`${API_BASE_URL}/auth/profile`,
					{
						method: 'GET',
						headers: { authorization: `Bearer ${authToken}` },
					}
				);
				if (response.ok) {
					const data = await response.json();
					setIsAuthenticated(true);

					// SUPER_ADMIN is a backend concept (cross-organization
					// management); the dashboard treats it as a regular admin.
					const role = data.data.user?.role;
					setUserRole(role === 'SUPER_ADMIN' ? 'ADMIN' : role);
				} else {
					setIsAuthenticated(false);
					setUserRole(null);
				}
			} catch (error) {
				setIsAuthenticated(false);
				setUserRole(null);
			}
		};
		checkAuth();
	}, []);

	if (isAuthenticated === null) {
		return (
			<div className="h-screen w-screen flex items-center justify-center">
				<Icon
					name="loader"
					className="animate-spin duration-2000 size-20 text-blue-500"
				/>
			</div>
		);
	}
	if (!isAuthenticated) {
		return <Navigate to="/login" state={{ from: location }} replace />;
	}

	return (
		<UserRoleContext.Provider
			value={{
				isAdmin: userRole === 'ADMIN',
				isCashier: userRole === 'USER',
				roleValue: userRole,
			}}
		>
			<Outlet />
		</UserRoleContext.Provider>
	);
}
